import { type MutableRefObject, useEffect, useRef } from 'react'
import {
  ctrlWheelMouseReadoutStepsForEvent,
  isCtrlWheelMouseNotchWheel,
} from '../helpers/ctrl-wheel-notch'
import { computeCtrlWheelFlushZoom } from '../helpers/ctrl-wheel-zoom'
import type { CenterCanvasConfig } from '../types/center-canvas-config'
import type { PendingCtrlWheelAnchor } from '../types/pending-ctrl-wheel-anchor'

/**
 * Ctrl/Cmd+wheel and trackpad pinch zoom (Chrome/Edge send pinch as wheel + ctrlKey; Safari uses
 * gesture events, see `use-safari-pinch-zoom`). Wheel input is coalesced into one microtask flush
 * that zooms toward the pointer.
 */
export function useCtrlWheelZoom({
  scrollRef,
  zoomRef,
  scaleRef,
  pendingCtrlWheelAnchorRef,
  onZoomChange,
  syncRulerScrollTransforms,
  computeContentPointAtClient,
}: {
  scrollRef: MutableRefObject<HTMLDivElement | null>
  zoomRef: MutableRefObject<number>
  scaleRef: MutableRefObject<number>
  pendingCtrlWheelAnchorRef: MutableRefObject<PendingCtrlWheelAnchor | null>
  onZoomChange: CenterCanvasConfig['onZoomChange']
  syncRulerScrollTransforms: () => void
  computeContentPointAtClient: (
    clientX: number,
    clientY: number,
    atScale: number
  ) => { contentX: number; contentY: number } | null
}) {
  /** Trackpad pinch: raw pixel `deltaY` summed for one rAF flush (`exp(-k·δ)`). */
  const ctrlWheelZoomAccumRef = useRef(0)

  /** Mouse wheel: net count of notches (±1 each) × the readout step (`ctrl-wheel-zoom.ts`) applied in flush. */
  const ctrlWheelMouseReadoutStepCountRef = useRef(0)

  /** Mouse-wheel cadence (same-direction inter‑event ms) for velocity scaling of readout steps. */
  const ctrlWheelLastMouseNotchMsRef = useRef(0)
  const ctrlWheelLastMouseNotchSignRef = useRef(0)

  /** Coalesces ctrl+wheel into one `queueMicrotask` flush (lower latency than rAF’s next-paint wait). */
  const ctrlWheelZoomFlushScheduledRef = useRef(false)

  /** Last pointer position for a ctrl+wheel gesture (used to zoom toward the cursor). */
  const ctrlWheelLastClientRef = useRef({ clientX: 0, clientY: 0 })

  // Ctrl/Cmd+wheel and trackpad pinch (Chrome/Edge send pinch as wheel + ctrlKey); Safari uses gesture* (`use-safari-pinch-zoom`)
  useEffect(() => {
    const scroller = scrollRef.current

    if (!scroller) return

    let ctrlWheelZoomEffectActive = true

    const flushCtrlWheelZoom = () => {
      ctrlWheelZoomFlushScheduledRef.current = false

      if (!ctrlWheelZoomEffectActive) return

      const rawMouseSteps = ctrlWheelMouseReadoutStepCountRef.current
      const rawAccum = ctrlWheelZoomAccumRef.current

      ctrlWheelMouseReadoutStepCountRef.current = 0
      ctrlWheelZoomAccumRef.current = 0

      if (rawMouseSteps === 0 && rawAccum === 0) return

      const initial = zoomRef.current === 0 ? scaleRef.current : zoomRef.current
      const nextZoom = computeCtrlWheelFlushZoom(rawMouseSteps, rawAccum, initial)

      if (nextZoom !== +initial.toFixed(3)) {
        if (zoomRef.current !== 0) {
          const { clientX, clientY } = ctrlWheelLastClientRef.current
          const point = computeContentPointAtClient(clientX, clientY, initial)

          if (point) {
            pendingCtrlWheelAnchorRef.current = {
              contentX: point.contentX,
              contentY: point.contentY,
              clientX,
              clientY,
              to: nextZoom,
            }
          }
        }

        onZoomChange?.(nextZoom)
      }
    }

    const scheduleCtrlWheelZoomFlush = () => {
      if (ctrlWheelZoomFlushScheduledRef.current) return

      ctrlWheelZoomFlushScheduledRef.current = true
      queueMicrotask(() => {
        flushCtrlWheelZoom()
      })
    }

    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return
      e.preventDefault()

      ctrlWheelLastClientRef.current = { clientX: e.clientX, clientY: e.clientY }

      if (e.deltaY === 0) return

      if (isCtrlWheelMouseNotchWheel(e)) {
        const now = performance.now()
        const sig = Math.sign(e.deltaY)
        const prevT = ctrlWheelLastMouseNotchMsRef.current
        const prevS = ctrlWheelLastMouseNotchSignRef.current
        const steps = ctrlWheelMouseReadoutStepsForEvent(sig, prevT, prevS, now)

        ctrlWheelLastMouseNotchMsRef.current = now
        ctrlWheelLastMouseNotchSignRef.current = sig

        // Match trackpad / browser: scroll up (negative deltaY) → zoom in; scroll down → zoom out.
        ctrlWheelMouseReadoutStepCountRef.current -= sig * steps
      } else {
        ctrlWheelZoomAccumRef.current += e.deltaY
      }

      scheduleCtrlWheelZoomFlush()
    }

    scroller.addEventListener('wheel', onWheel, { passive: false })
    return () => {
      ctrlWheelZoomEffectActive = false
      ctrlWheelZoomFlushScheduledRef.current = false

      ctrlWheelZoomAccumRef.current = 0
      ctrlWheelMouseReadoutStepCountRef.current = 0
      ctrlWheelLastMouseNotchMsRef.current = 0
      ctrlWheelLastMouseNotchSignRef.current = 0
      scroller.removeEventListener('wheel', onWheel)
    }
  }, [
    onZoomChange,
    syncRulerScrollTransforms,
    computeContentPointAtClient,
    scrollRef,
    zoomRef,
    scaleRef,
    pendingCtrlWheelAnchorRef,
  ])
}
